import React, { useMemo, useState } from 'react';

import { css } from '@emotion/css';
import { SearchForecastDTO } from '@recurrency/core-api-schema/dist/search/getSearchForecasts';
import { Result } from 'antd';
import moment from 'moment';
import { colors } from 'theme/colors';

import { Button } from 'components/Button';
import { FlexSpace } from 'components/FlexSpace';
import { Modal } from 'components/Modal';
import { TextArea } from 'components/TextArea';

import { getGlobalAppState } from 'hooks/useGlobalApp';

import { formatIdNameObj } from 'utils/formatting';
import { routes } from 'utils/routes';
import { track, TrackEvent } from 'utils/track';

import { IdNameObj } from 'types/legacy-api';

import { getMergedForecastRecords } from './sidePane.utils';

interface ForecastCell {
  date: string;
  quantity: number;
}

interface YearRow {
  year: string;
  cells: (ForecastCell | null)[];
}

function buildYearRows(forecastRecord: SearchForecastDTO): YearRow[] {
  const byYear: Record<string, Record<number, ForecastCell>> = {};

  forecastRecord.forecast_dates.slice(0, 18).forEach((date, i) => {
    const year = moment(date).format('YYYY');
    const monthIdx = moment(date).month();
    if (!byYear[year]) byYear[year] = {};
    byYear[year][monthIdx] = { date, quantity: forecastRecord.forecast_demand[i] ?? 0 };
  });

  return Object.keys(byYear)
    .sort()
    .map((year) => ({
      year,
      cells: Array.from({ length: 12 }, (_, i) => byYear[year][i] ?? null),
    }));
}

const headerCellClass = css`
  text-align: center;
  padding: 6px 4px;
  font-weight: 500;
  min-width: 44px;
  color: rgba(0, 0, 0, 0.45);
`;

export function ReportForecastModal({
  itemId,
  itemUid,
  locations,
  forecastRecords,
  onClose,
}: {
  itemId: string;
  itemUid: string;
  locations: IdNameObj[];
  forecastRecords: SearchForecastDTO[] | undefined;
  onClose: () => void;
}) {
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [details, setDetails] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mergedRecord = useMemo(() => getMergedForecastRecords(forecastRecords), [forecastRecords]);
  const yearRows = mergedRecord ? buildYearRows(mergedRecord) : [];
  const months = moment.monthsShort();

  const canSubmit = selectedDates.size >= 1 && details.trim().length >= 20;

  function toggleDate(date: string) {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  }

  function handleSubmit() {
    const { activeTenant, activeUser } = getGlobalAppState();
    const selectedDatesArr = Array.from(selectedDates);
    const selectedForecastValues = selectedDatesArr.map((date) => {
      const idx = mergedRecord?.forecast_dates.indexOf(date) ?? -1;
      return { date, quantity: idx >= 0 ? mergedRecord?.forecast_demand[idx] ?? 0 : 0 };
    });
    track(TrackEvent.DemandPlanning_Forecast_Flag, {
      tenantId: activeTenant.id,
      tenantSlug: activeTenant.slug,
      userEmail: activeUser.email,
      itemUid,
      itemId,
      locationId: locations[0].foreignId,
      locationName: locations[0].name,
      demandPattern: mergedRecord?.demand_pattern,
      demandPredictability: mergedRecord?.predictability_tag,
      unitOfMeasure: mergedRecord?.unit_of_measure ?? '',
      selectedForecastValues,
      details: details.trim(),
      recordUrl:
        window.location.origin +
        routes.demandPlanning.planning({
          where: { location: [formatIdNameObj(locations[0])] },
          query: itemId,
        }),
    });
    setSubmitted(true);
  }

  return (
    <Modal
      visible
      title="Report Forecast"
      onCancel={onClose}
      width={820}
      destroyOnClose
      footer={
        submitted ? null : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" disabled={!canSubmit} onClick={handleSubmit}>
              Report Forecast
            </Button>
          </>
        )
      }
    >
      {submitted ? (
        <Result
          status="success"
          title="Thank you for your report"
          subTitle="Our engineering team will use this as a data point for future forecast improvements."
          extra={
            <Button type="primary" onClick={onClose}>
              Close
            </Button>
          }
        />
      ) : (
        <FlexSpace direction="column" gap={20} fullWidth>
          <span>
            Flag a forecast that looks incorrect. Select the months that seem off, describe what you expected instead,
            and our team will use these reports to improve forecast accuracy over time.
          </span>

          {yearRows.length > 0 && (
            <div>
              <div
                className={css`
                  font-weight: bold;
                  margin-bottom: 8px;
                `}
              >
                Select the forecast months that look wrong
              </div>
              <div
                className={css`
                  overflow-x: auto;
                `}
              >
                <table
                  className={css`
                    border-collapse: separate;
                    border-spacing: 2px;
                    width: 100%;
                    font-size: 13px;
                  `}
                >
                  <thead>
                    <tr>
                      <th
                        className={css`
                          text-align: left;
                          padding: 6px 10px;
                          color: rgba(0, 0, 0, 0.45);
                          font-weight: 500;
                        `}
                      >
                        Year
                      </th>
                      {months.map((m) => (
                        <th key={m} className={headerCellClass}>
                          {m}
                        </th>
                      ))}
                      <th className={headerCellClass}>UOM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearRows.map((row) => (
                      <tr key={row.year}>
                        <td
                          className={css`
                            padding: 6px 10px;
                            font-weight: 600;
                          `}
                        >
                          {row.year}
                        </td>
                        {row.cells.map((cell, i) => {
                          const isSelected = !!cell && selectedDates.has(cell.date);
                          return (
                            <td
                              key={i}
                              className={css`
                                text-align: center;
                                padding: 0;
                              `}
                            >
                              {cell ? (
                                <div
                                  onClick={() => toggleDate(cell.date)}
                                  className={css`
                                    padding: 5px 4px;
                                    border-radius: 4px;
                                    cursor: pointer;
                                    user-select: none;
                                    background-color: ${isSelected ? colors.primary[200] : 'transparent'};
                                    font-weight: ${isSelected ? '600' : 'normal'};
                                    outline: ${isSelected ? `2px solid ${colors.primary[500]}` : 'none'};
                                    outline-offset: -2px;
                                    &:hover {
                                      background-color: ${!isSelected ? colors.primary[100] : undefined};
                                    }
                                  `}
                                >
                                  {cell.quantity}
                                </div>
                              ) : (
                                <div
                                  className={css`
                                    padding: 5px 4px;
                                    color: ${colors.neutral[400]};
                                    background-color: ${colors.neutral[100]};
                                    border-radius: 4px;
                                  `}
                                >
                                  —
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td
                          className={css`
                            text-align: center;
                            padding: 5px 8px;
                            color: rgba(0, 0, 0, 0.45);
                          `}
                        >
                          {mergedRecord?.unit_of_measure}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedDates.size > 0 && (
                <div
                  className={css`
                    margin-top: 6px;
                    font-size: 12px;
                    color: ${colors.primary[500]};
                  `}
                >
                  {selectedDates.size} month{selectedDates.size !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}

          <div>
            <div
              className={css`
                font-weight: bold;
                margin-bottom: 4px;
              `}
            >
              What did you expect instead?
            </div>
            <p
              className={css`
                margin: 0 0 8px 0;
                color: rgba(0, 0, 0, 0.45);
                font-size: 12px;
              `}
            >
              Use specific numbers and timeframes (e.g., "We usually sell 8–10 units per month, so April 2026 and May
              2026 should be closer to 10 units").
            </p>
            <TextArea
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe what you expected…"
              maxLength={300}
              showCount
              className={css`
                .ant-input {
                  border-radius: 8px;
                }
              `}
            />
            {details.trim().length < 20 && (
              <div
                className={css`
                  margin-top: 4px;
                  font-size: 12px;
                  color: rgba(0, 0, 0, 0.45);
                `}
              >
                {details.trim().length === 0
                  ? 'At least 20 characters required'
                  : `${20 - details.trim().length} more character${20 - details.trim().length !== 1 ? 's' : ''} needed`}
              </div>
            )}
          </div>
        </FlexSpace>
      )}
    </Modal>
  );
}
